import { useCallback, useEffect, useRef, useState } from 'react';
import { ChartNoAxesCombined, ClipboardList, FilePenLine, Settings } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './css/Surveys.css';

const Surveys = () => {
  const { hasPermission } = useAuth();
  const { surveyId: surveyIdParam } = useParams();
  const targetSurveyId = surveyIdParam ? parseInt(surveyIdParam, 10) : null;
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const canFill = hasPermission('survey:fill');
  const canViewResults = hasPermission('survey:view_results');
  const canManage = hasPermission('survey:manage');

  const getInitialTab = () => {
    if (canFill) return 'fill';
    if (canViewResults) return 'results';
    if (canManage) return 'manage';
    return 'fill';
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || (targetSurveyId && canFill ? 'fill' : getInitialTab());

  const formatErrorMessage = (error) => {
    const detail = error.response?.data?.detail;
    
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    }
    
    if (typeof detail === 'string') {
      return detail;
    }
    
    return 'Wystąpił nieoczekiwany błąd';
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="page-container surveys-page">
      <div className="page-header">
        <h2 className="module-page-title"><ClipboardList aria-hidden="true" /> Ankiety</h2>
        <p>System zarządzania ankietami</p>
      </div>

      {error && (
        <div className="error-message">
          {typeof error === 'string' ? error : JSON.stringify(error)}
          <button onClick={clearMessages} className="close-btn">×</button>
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
          <button onClick={clearMessages} className="close-btn">×</button>
        </div>
      )}

      <div className="tabs">
        {canFill && (
          <button
            className={`tab-button ${activeTab === 'fill' ? 'active' : ''}`}
            onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'fill'); return next; })}
          >
            <FilePenLine aria-hidden="true" /> Wypełnij ankietę
          </button>
        )}
        {canViewResults && (
          <button
            className={`tab-button ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'results'); return next; })}
          >
            <ChartNoAxesCombined aria-hidden="true" /> Wyniki
          </button>
        )}
        {canManage && (
          <button
            className={`tab-button ${activeTab === 'manage' ? 'active' : ''}`}
            onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'manage'); return next; })}
          >
            <Settings aria-hidden="true" /> Zarządzanie
          </button>
        )}
      </div>

      <div className="page-content">
        {activeTab === 'fill' && canFill && (
          <FillSurveyTab
            setError={setError}
            setSuccess={setSuccess}
            clearMessages={clearMessages}
            formatErrorMessage={formatErrorMessage}
            targetSurveyId={targetSurveyId}
          />
        )}
        {activeTab === 'results' && canViewResults && (
          <ResultsTab setError={setError} clearMessages={clearMessages} formatErrorMessage={formatErrorMessage} />
        )}
        {activeTab === 'manage' && canManage && (
          <ManageTab setError={setError} setSuccess={setSuccess} clearMessages={clearMessages} formatErrorMessage={formatErrorMessage} />
        )}
      </div>
    </div>
  );
};

const FillSurveyTab = ({ setError, setSuccess, clearMessages, formatErrorMessage, targetSurveyId }) => {
  const { user } = useAuth();
  const [myAssignments, setMyAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchMyAssignments = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();
      const response = await api.get('/surveys/my-assignments');
      setMyAssignments(response.data);
    } catch (err) {
      setError('Nie udało się załadować przypisanych ankiet');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clearMessages, setError]);

  useEffect(() => {
    fetchMyAssignments();
  }, [fetchMyAssignments]);

  // Auto-select survey when targetSurveyId is provided via URL
  useEffect(() => {
    if (!targetSurveyId || loading || selectedSurvey) return;
    const target = myAssignments.find(s => s.id === targetSurveyId);
    if (target && target.is_available !== false) {
      setSelectedSurvey(target);
    }
  }, [loading, targetSurveyId, myAssignments, selectedSurvey]);

  const handleAnswerChange = (fieldId, fieldType, value) => {
    setAnswers(prev => {
      const answer = { field_id: fieldId };
      
      if (fieldType === 'rating') {
        answer.value_number = parseInt(value);
      } else if (fieldType === 'text') {
        answer.value_text = value;
      } else if (fieldType === 'boolean') {
        answer.value_boolean = value;
      } else if (fieldType === 'multiple_choice') {
        answer.value_text = value;
      }
      
      return { ...prev, [fieldId]: answer };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSurvey) return;

    // Validate required fields
    const requiredFields = selectedSurvey.fields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !answers[f.id]);
    
    if (missingFields.length > 0) {
      setError(`Wypełnij wymagane pola: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    try {
      setSubmitting(true);
      clearMessages();
      
      const payload = {
        answers: Object.values(answers)
      };
      
      await api.post(`/surveys/${selectedSurvey.id}/submit`, payload);
      setSuccess('Ankieta została wysłana pomyślnie!');
      setSelectedSurvey(null);
      setAnswers({});
      fetchMyAssignments();
    } catch (err) {
      setError(formatErrorMessage(err));
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Ładowanie...</div>;

  const pendingSurveys = myAssignments.filter(s => {
    const currentUserEntry = s.assigned_users?.find(u => u.id === user?.id);
    return !currentUserEntry?.has_submitted;
  });

  if (pendingSurveys.length === 0) {
    return (
      <div className="empty-state">
        <p>✅ Nie masz żadnych oczekujących ankiet do wypełnienia.</p>
      </div>
    );
  }

  return (
    <div className="fill-survey-tab">
      <h3>Przypisane ankiety</h3>

      {!selectedSurvey ? (
        <div className="surveys-list">
          {pendingSurveys.map(survey => {
            const isAvailable = survey.is_available !== false;
            const startTime = new Date(survey.start_time).toLocaleString('pl-PL');
            
            return (
              <div key={survey.id} className="survey-card">
                <div className="survey-info">
                  <h4>{survey.name}</h4>
                  {survey.description && <p className="survey-description">{survey.description}</p>}
                  <p>
                    <strong>Okres:</strong>{' '}
                    {new Date(survey.start_time).toLocaleDateString('pl-PL')} -{' '}
                    {new Date(survey.end_time).toLocaleDateString('pl-PL')}
                  </p>
                  <p className="deadline">
                    <strong>Termin wypełnienia:</strong>{' '}
                    {new Date(survey.end_time).toLocaleString('pl-PL')}
                  </p>
                  {!isAvailable && (
                    <p className="warning-message" style={{color: '#f39c12', fontWeight: 'bold'}}>
                      ⏰ Ankieta będzie dostępna od: {startTime}
                    </p>
                  )}
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => setSelectedSurvey(survey)}
                  disabled={!isAvailable}
                  title={!isAvailable ? 'Ankieta będzie dostępna po czasie rozpoczęcia' : ''}
                >
                  {isAvailable ? 'Wypełnij' : 'Niedostępna'}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="survey-form">
          <div className="form-header">
            <h4>{selectedSurvey.name}</h4>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSelectedSurvey(null);
                setAnswers({});
              }}
            >
              Powrót
            </button>
          </div>

          {selectedSurvey.description && (
            <p className="survey-description">{selectedSurvey.description}</p>
          )}

          <form onSubmit={handleSubmit}>
            {selectedSurvey.fields.map(field => (
              <DynamicField
                key={field.id}
                field={field}
                value={answers[field.id]}
                onChange={(value) => handleAnswerChange(field.id, field.field_type, value)}
              />
            ))}

            <button
              type="submit"
              className="btn btn-success"
              disabled={submitting}
            >
              {submitting ? 'Wysyłanie...' : 'Wyślij ankietę'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

const DynamicField = ({ field, value, onChange }) => {
  const { field_type, label, description, required, config } = field;

  if (field_type === 'rating') {
    const min = config?.min || 1;
    const max = config?.max || 5;
    const currentValue = value?.value_number;
    
    return (
      <div className="rating-field">
        <label>
          {label} {required && <span className="required">*</span>}
        </label>
        {description && <p className="field-description">{description}</p>}
        <div className="rating-buttons">
          {Array.from({ length: max - min + 1 }, (_, i) => min + i).map(val => (
            <button
              key={val}
              type="button"
              className={`rating-btn ${currentValue === val ? 'active' : ''}`}
              onClick={() => onChange(val)}
            >
              {val}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (field_type === 'text') {
    const maxLength = config?.max_length;
    
    return (
      <div className="form-group">
        <label>
          {label} {required && <span className="required">*</span>}
        </label>
        {description && <p className="field-description">{description}</p>}
        <input
          type="text"
          value={value?.value_text || ''}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          required={required}
          className="form-control"
        />
      </div>
    );
  }

  if (field_type === 'boolean') {
    const current = value?.value_boolean;
    return (
      <div className="form-group">
        <label>
          {label} {required && <span className="required">*</span>}
        </label>
        {description && <p className="field-description">{description}</p>}
        <div className="boolean-buttons">
          <button
            type="button"
            className={`boolean-btn boolean-btn-yes${current === true ? ' active' : ''}`}
            onClick={() => onChange(true)}
          >
            ✓ Tak
          </button>
          <button
            type="button"
            className={`boolean-btn boolean-btn-no${current === false ? ' active' : ''}`}
            onClick={() => onChange(false)}
          >
            ✗ Nie
          </button>
        </div>
      </div>
    );
  }

  if (field_type === 'multiple_choice') {
    const options = config?.options || [];
    const selected = value?.value_text;
    return (
      <div className="form-group">
        <label>
          {label} {required && <span className="required">*</span>}
        </label>
        {description && <p className="field-description">{description}</p>}
        <div className="mc-options">
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              className={`mc-option-btn${selected === opt ? ' active' : ''}`}
              onClick={() => onChange(opt)}
            >
              {opt}
            </button>
          ))}
        </div>
        {options.length === 0 && (
          <p className="field-hint">Brak opcji do wyboru.</p>
        )}
      </div>
    );
  }

  return null;
};

const ResultsTab = ({ setError, clearMessages, formatErrorMessage }) => {
  const [overallStats, setOverallStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyDetails, setSurveyDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  const fetchOverallStats = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();
      const response = await api.get('/surveys/statistics/overall');
      setOverallStats(response.data);
    } catch (err) {
      setError('Nie udało się załadować statystyk');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clearMessages, setError]);

  const fetchSurveyDetails = useCallback(async (surveyId) => {
    if (surveyDetails[surveyId]) return;
    
    try {
      setLoadingDetails(prev => ({ ...prev, [surveyId]: true }));
      const response = await api.get(`/surveys/${surveyId}`);
      setSurveyDetails(prev => ({ ...prev, [surveyId]: response.data }));
    } catch (err) {
      setError('Nie udało się załadować szczegółów ankiety');
      console.error(err);
    } finally {
      setLoadingDetails(prev => ({ ...prev, [surveyId]: false }));
    }
  }, [surveyDetails, setError]);

  const handleSurveyExpand = (surveyId) => {
    const newSelectedSurvey = selectedSurvey === surveyId ? null : surveyId;
    setSelectedSurvey(newSelectedSurvey);
    
    if (newSelectedSurvey && !surveyDetails[surveyId]) {
      fetchSurveyDetails(surveyId);
    }
  };

  const handleExportPDF = async (surveyId, surveyName) => {
    try {
      const response = await api.get(`/surveys/${surveyId}/export-pdf`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Survey_${surveyName.replace(/ /g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Nie udało się wyeksportować PDF');
      console.error(err);
    }
  };

  useEffect(() => {
    fetchOverallStats();
  }, [fetchOverallStats]);

  if (loading) return <div className="loading">Ładowanie...</div>;
  if (!overallStats) return null;

  const { survey_stats } = overallStats;

  return (
    <div className="results-tab">
      <div className="survey-stats">
        <h3>Statystyki ankiet</h3>
        {survey_stats.map(stat => (
          <div key={stat.survey_id} className="survey-stat-card">
            <div className="survey-stat-header" onClick={() => handleSurveyExpand(stat.survey_id)}>
              <h4>{stat.survey_name}</h4>
              <div className="survey-stat-summary">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportPDF(stat.survey_id, stat.survey_name);
                  }}
                  title="Eksportuj do PDF"
                >
                  📄 PDF
                </button>
                <span className="completion-rate">
                  {stat.total_submitted}/{stat.total_assigned} wypełnionych
                </span>
                <span className="expand-icon">
                  {selectedSurvey === stat.survey_id ? '▼' : '▶'}
                </span>
              </div>
            </div>

            {selectedSurvey === stat.survey_id && (
              <div className="survey-stat-details">
                <div className="field-statistics">
                  {stat.field_statistics.map(fieldStat => (
                    <FieldStatDisplay key={fieldStat.field_id} fieldStat={fieldStat} />
                  ))}
                </div>

                {loadingDetails[stat.survey_id] ? (
                  <div className="loading-details">Ładowanie szczegółów...</div>
                ) : surveyDetails[stat.survey_id] && surveyDetails[stat.survey_id].responses && surveyDetails[stat.survey_id].responses.length > 0 ? (
                  <SurveyResponsesTable survey={surveyDetails[stat.survey_id]} />
                ) : null}

                {stat.missing_users.length > 0 && (
                  <div className="missing-users">
                    <h5>Nie wypełnili ankiety:</h5>
                    <ul>
                      {stat.missing_users.map(user => (
                        <li key={user.id}>{user.full_name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const FieldStatDisplay = ({ fieldStat }) => {
  const { field_label, field_type, average, total_responses, text_sample } = fieldStat;

  if (field_type === 'rating') {
    return (
      <div className="field-stat">
        <div className="field-stat-header">
          <span className="field-stat-label">{field_label}</span>
          <span className="field-stat-value">
            {average !== null ? average.toFixed(2) : '—'}
          </span>
        </div>
        {average !== null && (
          <div className="rating-bar-track">
            <div
              className="rating-bar-fill"
              style={{
                width: `${(average / 5) * 100}%`,
                backgroundColor: getRatingColor(average)
              }}
            />
          </div>
        )}
        <div className="field-stat-count">Odpowiedzi: {total_responses}</div>
      </div>
    );
  }

  if (field_type === 'boolean') {
    const trueCount  = text_sample ? parseInt(text_sample[0] || '0') : 0;
    const falseCount = text_sample ? parseInt(text_sample[1] || '0') : 0;
    const total = trueCount + falseCount || 1;
    const labels = { yes: 'Tak', no: 'Nie' };
    return (
      <div className="field-stat">
        <div className="field-stat-header">
          <span className="field-stat-label">{field_label}</span>
        </div>
        <div className="bool-stat-row">
          <span className="bool-stat-yes">{labels.yes}: <strong>{trueCount}</strong> ({Math.round(trueCount / total * 100)}%)</span>
          <span className="bool-stat-no">{labels.no}: <strong>{falseCount}</strong> ({Math.round(falseCount / total * 100)}%)</span>
        </div>
        <div className="field-stat-count">Odpowiedzi: {total_responses}</div>
      </div>
    );
  }

  if (field_type === 'multiple_choice') {
    const counts = {};
    (text_sample || []).forEach(v => { counts[v] = (counts[v] || 0) + 1; });
    const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
    return (
      <div className="field-stat">
        <div className="field-stat-header">
          <span className="field-stat-label">{field_label}</span>
        </div>
        <div className="mc-stat-bars">
          {Object.entries(counts).map(([opt, count]) => (
            <div key={opt} className="mc-stat-row">
              <span className="mc-stat-label">{opt}</span>
              <div className="mc-bar-track">
                <div className="mc-bar-fill" style={{ width: `${Math.round(count / total * 100)}%` }} />
              </div>
              <span className="mc-stat-count">{count}</span>
            </div>
          ))}
        </div>
        <div className="field-stat-count">Odpowiedzi: {total_responses}</div>
      </div>
    );
  }

  if (field_type === 'text') {
    return (
      <div className="field-stat">
        <div className="field-stat-header">
          <span className="field-stat-label">{field_label}</span>
        </div>
        <div className="field-stat-count">Odpowiedzi: {total_responses}</div>
        {text_sample && text_sample.length > 0 && (
          <div className="text-sample">
            <strong>Przykładowe odpowiedzi:</strong>
            <ul>
              {text_sample.map((text, idx) => (
                <li key={idx}>{text}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="field-stat">
      <div className="field-stat-header">
        <span className="field-stat-label">{field_label}</span>
      </div>
      <div className="field-stat-count">Odpowiedzi: {total_responses}</div>
    </div>
  );
};

const SurveyResponsesTable = ({ survey }) => {
  return (
    <div className="responses-table-container">
      <h5>Szczegółowe odpowiedzi:</h5>
      <table className="responses-table">
        <thead>
          <tr>
            <th>Pracownik</th>
            {survey.fields.map(field => (
              <th key={field.id}>{field.label}</th>
            ))}
            <th>Data wypełnienia</th>
          </tr>
        </thead>
        <tbody>
          {survey.responses.map(response => {
            const answerMap = {};
            response.answers.forEach(answer => {
              answerMap[answer.field_id] = answer;
            });

            return (
              <tr key={response.id}>
                <td>{response.user_name || 'Nieznany'}</td>
                {survey.fields.map(field => {
                  const answer = answerMap[field.id];
                  let displayValue = '-';
                  
                  if (answer) {
                    if (answer.value_number !== null) {
                      displayValue = answer.value_number;
                    } else if (answer.value_text) {
                      displayValue = answer.value_text.length > 50 
                        ? answer.value_text.substring(0, 50) + '...' 
                        : answer.value_text;
                    } else if (answer.value_boolean !== null) {
                      displayValue = answer.value_boolean ? 'Tak' : 'Nie';
                    }
                  }

                  return <td key={field.id}>{displayValue}</td>;
                })}
                <td>{new Date(response.submitted_at).toLocaleString('pl-PL')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ManageTab = ({ setError, setSuccess, clearMessages, formatErrorMessage }) => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [usersPerDepartment, setUsersPerDepartment] = useState({});
  const [startNow, setStartNow] = useState(false);
  
  const [newSurvey, setNewSurvey] = useState({
    name: '',
    description: '',
    start_time: '',
    end_time: '',
    send_to_all: false,
    departments: [],
    selected_user_ids: null,
    fields: [],
  });
  
  const [editFormData, setEditFormData] = useState({
    send_to_all: false,
    departments: [],
    selected_user_ids: null,
  });

  const fetchSurveys = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();
      const response = await api.get('/surveys');
      setSurveys(response.data.surveys);
    } catch (err) {
      setError('Nie udało się załadować ankiet');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clearMessages, setError]);

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await api.get('/surveys/departments');
      setDepartments(response.data);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  }, []);

  const fetchUsersPerDepartment = useCallback(async () => {
    try {
      const response = await api.get('/surveys/users-by-department');
      setUsersPerDepartment(response.data);
    } catch (err) {
      console.error('Error fetching users per department:', err);
    }
  }, []);

  useEffect(() => {
    fetchSurveys();
    fetchDepartments();
    fetchUsersPerDepartment();
  }, [fetchSurveys, fetchDepartments, fetchUsersPerDepartment]);

  const addField = () => {
    const newField = {
      field_key: `field_${Date.now()}`,
      field_type: 'rating',
      label: '',
      description: '',
      required: true,
      order: newSurvey.fields.length,
      config: { min: 1, max: 5 },
    };
    setNewSurvey(prev => ({ ...prev, fields: [...prev.fields, newField] }));
  };

  const removeField = (index) => {
    setNewSurvey(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  const updateField = (index, updates) => {
    setNewSurvey(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => i === index ? { ...field, ...updates } : field),
    }));
  };

  const handleCreateSurvey = async (e) => {
    e.preventDefault();

    if (!newSurvey.send_to_all && newSurvey.departments.length === 0) {
      setError('Musisz wybrać przynajmniej jeden dział lub zaznaczyć "Wyślij do wszystkich"');
      return;
    }
    
    if (newSurvey.fields.length === 0) {
      setError('Musisz dodać przynajmniej jedno pole');
      return;
    }

    try {
      clearMessages();
      
      const surveyData = { ...newSurvey };
      if (startNow) {
        surveyData.start_time = new Date().toISOString();
      }
      
      await api.post('/surveys', surveyData);
      setSuccess('Ankieta została utworzona pomyślnie!');
      setShowCreateForm(false);
      setNewSurvey({
        name: '',
        description: '',
        start_time: '',
        end_time: '',
        send_to_all: false,
        departments: [],
        selected_user_ids: null,
        fields: [],
      });
      setStartNow(false);
      fetchSurveys();
    } catch (err) {
      setError(formatErrorMessage(err));
    }
  };

  const handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę ankietę?')) return;

    try {
      clearMessages();
      await api.delete(`/surveys/${surveyId}`);
      setSuccess('Ankieta została usunięta');
      fetchSurveys();
    } catch (err) {
      setError(formatErrorMessage(err));
    }
  };

  const handleEditSurvey = (survey) => {
    setEditingSurvey(survey);
    setEditFormData({
      send_to_all: false,
      departments: survey.departments?.filter(d => d !== '__all__') || [],
      selected_user_ids: null,
    });
  };

  const handleUpdateSurvey = async (e) => {
    e.preventDefault();

    if (!editFormData.send_to_all && editFormData.departments.length === 0) {
      setError('Musisz wybrać przynajmniej jeden dział lub zaznaczyć "Wyślij do wszystkich"');
      return;
    }

    try {
      clearMessages();
      await api.put(`/surveys/${editingSurvey.id}`, editFormData);
      setSuccess('Ankieta została zaktualizowana');
      setEditingSurvey(null);
      fetchSurveys();
    } catch (err) {
      setError(formatErrorMessage(err));
    }
  };

  if (loading) return <div className="loading">Ładowanie...</div>;

  return (
    <div className="manage-tab">
      {!showCreateForm && !editingSurvey ? (
        <>
          <div className="manage-header">
            <h3>Ankiety</h3>
            <button
              className="btn btn-success"
              onClick={() => setShowCreateForm(true)}
            >
              + Nowa ankieta
            </button>
          </div>

          <div className="surveys-list">
            {surveys.map(survey => (
              <div key={survey.id} className="manage-survey-card">
                <div className="msc-body">
                  <div className="msc-title-row">
                    <h4 className="msc-title">{survey.name}</h4>
                    <span className={`status-badge ${survey.is_active ? 'badge-active' : 'badge-inactive'}`}>
                      {survey.is_active ? '● Aktywna' : '○ Nieaktywna'}
                    </span>
                  </div>
                  {survey.description && <p className="msc-desc">{survey.description}</p>}
                  <div className="msc-meta">
                    <span className="msc-meta-item">📅 {new Date(survey.start_time).toLocaleDateString('pl-PL')} – {new Date(survey.end_time).toLocaleDateString('pl-PL')}</span>
                    <span className="msc-meta-item">🏢 {survey.departments?.join(', ') || '—'}</span>
                    <span className="msc-meta-item"><ClipboardList size={15} strokeWidth={1.8} aria-hidden="true" /> {survey.fields?.length || 0} {survey.fields?.length === 1 ? 'pole' : 'pól'}</span>
                  </div>
                  <div className="msc-progress">
                    <div className="msc-progress-label">
                      <span>Wypełnienia</span>
                      <strong>{survey.total_submitted}/{survey.total_assigned} ({survey.submission_rate.toFixed(0)}%)</strong>
                    </div>
                    <div className="progress-track">
                      <div className="progress-fill" style={{ width: `${Math.min(survey.submission_rate, 100)}%` }} />
                    </div>
                  </div>
                </div>
                <div className="msc-actions">
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => handleEditSurvey(survey)}
                  >
                    Edytuj działy
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteSurvey(survey.id)}
                  >
                    Usuń
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : editingSurvey ? (
        <EditSurveyForm
          survey={editingSurvey}
          formData={editFormData}
          setFormData={setEditFormData}
          departments={departments}
          usersPerDepartment={usersPerDepartment}
          onSubmit={handleUpdateSurvey}
          onCancel={() => setEditingSurvey(null)}
        />
      ) : (
        <CreateSurveyForm
          formData={newSurvey}
          setFormData={setNewSurvey}
          departments={departments}
          usersPerDepartment={usersPerDepartment}
          startNow={startNow}
          setStartNow={setStartNow}
          onSubmit={handleCreateSurvey}
          onCancel={() => setShowCreateForm(false)}
          addField={addField}
          removeField={removeField}
          updateField={updateField}
        />
      )}
    </div>
  );
};

const RecipientSelector = ({
  formData,
  setFormData,
  departments,
  usersPerDepartment,
  sectionNumber = '3',
  initialAssignedIds = null,
}) => {
  const [userSelections, setUserSelections] = useState({});
  const initDoneRef = useRef(false);

  useEffect(() => {
    if (!initialAssignedIds || initDoneRef.current || Object.keys(usersPerDepartment).length === 0) return;
    initDoneRef.current = true;
    const init = {};
    for (const dept of formData.departments) {
      const users = usersPerDepartment[dept] || [];
      init[dept] = Object.fromEntries(users.map(u => [u.id, initialAssignedIds.has(u.id)]));
    }
    setUserSelections(init);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersPerDepartment]);

  const handleAddDept = (dept) => {
    if (formData.departments.includes(dept)) return;
    setFormData(prev => ({ ...prev, departments: [...prev.departments, dept] }));
    const users = usersPerDepartment[dept] || [];
    setUserSelections(prev => ({
      ...prev,
      [dept]: Object.fromEntries(users.map(u => [u.id, true])),
    }));
  };

  const handleRemoveDept = (dept) => {
    setFormData(prev => ({ ...prev, departments: prev.departments.filter(d => d !== dept) }));
    setUserSelections(prev => { const n = { ...prev }; delete n[dept]; return n; });
  };

  const handleToggleUser = (dept, userId) => {
    setUserSelections(prev => ({
      ...prev,
      [dept]: { ...prev[dept], [userId]: !(prev[dept]?.[userId] ?? true) },
    }));
  };

  const handleToggleAllInDept = (dept) => {
    const users = usersPerDepartment[dept] || [];
    const allSel = users.every(u => (userSelections[dept]?.[u.id] ?? true));
    setUserSelections(prev => ({
      ...prev,
      [dept]: Object.fromEntries(users.map(u => [u.id, !allSel])),
    }));
  };

  useEffect(() => {
    if (formData.send_to_all) return;
    let allSelected = true;
    const ids = [];
    for (const dept of formData.departments) {
      for (const u of usersPerDepartment[dept] || []) {
        if (userSelections[dept]?.[u.id] !== false) {
          ids.push(u.id);
        } else {
          allSelected = false;
        }
      }
    }
    const newIds = allSelected ? null : ids;
    setFormData(f => {
      if (JSON.stringify(f.selected_user_ids) === JSON.stringify(newIds)) return f;
      return { ...f, selected_user_ids: newIds };
    });
  }, [userSelections, formData.departments, formData.send_to_all, usersPerDepartment, setFormData]);

  const totalSelected = formData.send_to_all ? 0 : formData.departments.reduce(
    (acc, dept) => acc + (usersPerDepartment[dept] || []).filter(u => userSelections[dept]?.[u.id] !== false).length,
    0
  );

  return (
    <div className="form-section">
      <div className="section-header">
        <span className="section-number">{sectionNumber}</span>
        <h4>Odbiorcy</h4>
        {!formData.send_to_all && totalSelected > 0 && (
          <span className="section-badge">{totalSelected} {totalSelected === 1 ? 'osoba' : 'osób'}</span>
        )}
      </div>
      <div className="section-body">
        <label className="toggle-label" style={{ marginBottom: '16px' }}>
          <span className="toggle-switch">
            <input
              type="checkbox"
              checked={formData.send_to_all}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  send_to_all: e.target.checked,
                  departments: e.target.checked ? [] : prev.departments,
                  selected_user_ids: null,
                }));
                setUserSelections({});
              }}
            />
            <span className="toggle-slider"></span>
          </span>
          Wyślij do wszystkich użytkowników
        </label>
        {formData.send_to_all ? (
          <div className="send-all-notice">
            <span className="send-all-icon">👥</span>
            Ankieta zostanie wysłana do wszystkich aktywnych użytkowników w systemie.
          </div>
        ) : (
          <>
            <p className="section-hint">Wybierz działy i opcjonalnie wskaż konkretne osoby.</p>
            <div className="dept-chips">
              {departments.map(dept => (
                <button
                  key={dept}
                  type="button"
                  className={`dept-chip ${formData.departments.includes(dept) ? 'dept-chip-selected' : ''}`}
                  onClick={() => formData.departments.includes(dept) ? handleRemoveDept(dept) : handleAddDept(dept)}
                >
                  {formData.departments.includes(dept) && <span className="chip-check">✓</span>}
                  {dept}
                </button>
              ))}
            </div>
            {formData.departments.length === 0 && (
              <p className="validation-hint">Wybierz przynajmniej jeden dział</p>
            )}
            {formData.departments.map(dept => {
              const users = usersPerDepartment[dept] || [];
              const selCount = users.filter(u => userSelections[dept]?.[u.id] !== false).length;
              const allSel = users.length > 0 && users.every(u => userSelections[dept]?.[u.id] !== false);
              return (
                <div key={dept} className="dept-user-panel">
                  <div className="dup-header">
                    <span className="dup-dept-name">🏢 {dept}</span>
                    <span className="dup-count">{selCount} / {users.length} osób</span>
                    <button type="button" className="dup-toggle-all-btn" onClick={() => handleToggleAllInDept(dept)}>
                      {allSel ? 'Odznacz wszystkich' : 'Zaznacz wszystkich'}
                    </button>
                  </div>
                  <div className="dup-user-grid">
                    {users.map(u => {
                      const checked = userSelections[dept]?.[u.id] !== false;
                      return (
                        <label key={u.id} className={`dup-user-item${checked ? ' dup-selected' : ''}`}>
                          <input type="checkbox" checked={checked} onChange={() => handleToggleUser(dept, u.id)} />
                          <span className="dup-user-name">{u.full_name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

const CreateSurveyForm = ({
  formData,
  setFormData,
  departments,
  usersPerDepartment,
  startNow,
  setStartNow,
  onSubmit,
  onCancel,
  addField,
  removeField,
  updateField,
}) => {
  return (
    <div className="create-survey-form">
      <div className="csf-topbar">
        <div className="csf-topbar-left">
          <button type="button" className="back-btn" onClick={onCancel}>← Wróć</button>
          <h3>Nowa ankieta</h3>
        </div>
      </div>

      <form onSubmit={onSubmit}>
        {/* 1 — Podstawowe informacje */}
        <div className="form-section">
          <div className="section-header">
            <span className="section-number">1</span>
            <h4>Podstawowe informacje</h4>
          </div>
          <div className="section-body">
            <div className="form-group">
              <label htmlFor="name">Nazwa ankiety <span className="required">*</span></label>
              <input
                type="text"
                id="name"
                className="form-input form-input-lg"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="np. Ankieta jakości produkcji — Kwiecień 2026"
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Opis <span className="label-optional">opcjonalny</span></label>
              <textarea
                id="description"
                className="form-input"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Krótki opis celu ankiety dla wypełniających..."
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* 2 — Harmonogram */}
        <div className="form-section">
          <div className="section-header">
            <span className="section-number">2</span>
            <h4>Harmonogram</h4>
          </div>
          <div className="section-body">
            <label className="toggle-label">
              <span className="toggle-switch">
                <input
                  type="checkbox"
                  checked={startNow}
                  onChange={(e) => setStartNow(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </span>
              Rozpocznij natychmiast
            </label>
            <div className="form-row" style={{ marginTop: '16px' }}>
              <div className="form-group">
                <label htmlFor="start_time">Początek okresu <span className="required">*</span></label>
                <input
                  type="datetime-local"
                  id="start_time"
                  className="form-input"
                  value={formData.start_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                  required={!startNow}
                  disabled={startNow}
                />
                {startNow && <p className="field-hint">Ustawi się automatycznie na teraz</p>}
              </div>
              <div className="form-group">
                <label htmlFor="end_time">Termin wypełnienia <span className="required">*</span></label>
                <input
                  type="datetime-local"
                  id="end_time"
                  className="form-input"
                  value={formData.end_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* 3 — Odbiorcy */}
        <RecipientSelector
          formData={formData}
          setFormData={setFormData}
          departments={departments}
          usersPerDepartment={usersPerDepartment}
          sectionNumber="3"
        />

        {/* 4 — Pytania */}
        <div className="form-section">
          <div className="section-header">
            <span className="section-number">4</span>
            <h4>Pytania</h4>
            {formData.fields.length > 0 && (
              <span className="section-badge">
                {formData.fields.length} {formData.fields.length === 1 ? 'pytanie' : 'pytań'}
              </span>
            )}
            <button type="button" className="btn-add-field" onClick={addField}>
              + Dodaj pytanie
            </button>
          </div>
          <div className="section-body">
            {formData.fields.length === 0 ? (
              <div className="fields-empty-state">
                <div className="fields-empty-icon">📝</div>
                <p>Brak pytań. Kliknij <strong>+ Dodaj pytanie</strong> powyżej.</p>
              </div>
            ) : (
              <div className="fields-list">
                {formData.fields.map((field, index) => (
                  <FieldBuilder
                    key={index}
                    field={field}
                    index={index}
                    onUpdate={(updates) => updateField(index, updates)}
                    onRemove={() => removeField(index)}
                  />
                ))}
                <button type="button" className="btn-add-field-bottom" onClick={addField}>
                  + Dodaj kolejne pytanie
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="form-submit-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Anuluj</button>
          <button type="submit" className="btn btn-success">Utwórz ankietę</button>
        </div>
      </form>
    </div>
  );
};

const FIELD_TYPES = [
  { value: 'rating',          label: 'Ocena',             icon: '⭐' },
  { value: 'text',            label: 'Tekst',             icon: '✏️' },
  { value: 'boolean',         label: 'Tak / Nie',         icon: '👍' },
  { value: 'multiple_choice', label: 'Wielokrotny wybór', icon: '☰' },
];

const FieldBuilder = ({ field, index, onUpdate, onRemove }) => {
  const [expanded, setExpanded] = useState(!field.label);

  const typeInfo = FIELD_TYPES.find(t => t.value === field.field_type) || FIELD_TYPES[0];

  const handleTypeChange = (newType) => {
    const updates = { field_type: newType };
    if (newType === 'rating') {
      updates.config = { min: 1, max: 5 };
    } else if (newType === 'text') {
      updates.config = { max_length: 500 };
    } else if (newType === 'multiple_choice') {
      updates.config = { options: ['Opcja 1', 'Opcja 2', 'Opcja 3'] };
    } else {
      updates.config = null;
    }
    onUpdate(updates);
  };

  return (
    <div className={`field-builder-card ${expanded ? 'field-card-expanded' : ''}`}>
      <div className="field-card-header" onClick={() => setExpanded(e => !e)}>
        <div className="field-card-left">
          <span className="field-index">{index + 1}</span>
          <span className="field-type-badge">{typeInfo.icon} {typeInfo.label}</span>
          {!expanded && (
            <span className="field-label-preview">
              {field.label || <em className="field-label-empty">Uzupełnij etykietę...</em>}
            </span>
          )}
          {field.required && <span className="field-required-badge">*</span>}
        </div>
        <div className="field-card-right">
          <button
            type="button"
            className="field-remove-btn"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            title="Usuń pole"
          >
            ✕
          </button>
          <span className="field-expand-icon">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="field-card-body">
          <div className="form-group">
            <label>Typ pytania</label>
            <div className="type-selector">
              {FIELD_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  className={`type-btn ${field.field_type === type.value ? 'type-btn-active' : ''}`}
                  onClick={() => handleTypeChange(type.value)}
                >
                  <span className="type-btn-icon">{type.icon}</span>
                  <span className="type-btn-label">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Treść pytania <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              value={field.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              placeholder="np. Jak oceniasz jakość produkcji?"
              required
            />
          </div>

          <div className="form-group">
            <label>Opis <span className="label-optional">opcjonalny</span></label>
            <input
              type="text"
              className="form-input"
              value={field.description || ''}
              onChange={(e) => onUpdate({ description: e.target.value })}
              placeholder="Dodatkowe wskazówki dla wypełniającego"
            />
          </div>

          <label className="toggle-label toggle-label-sm">
            <span className="toggle-switch toggle-switch-sm">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => onUpdate({ required: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </span>
            Pole wymagane
          </label>

          {(field.field_type === 'rating') && field.config && (
            <div className="form-row config-row">
              <div className="form-group">
                <label>Wartość min</label>
                <input
                  type="number"
                  className="form-input"
                  value={field.config.min ?? 1}
                  onChange={(e) => onUpdate({ config: { ...field.config, min: parseInt(e.target.value) } })}
                />
              </div>
              <div className="form-group">
                <label>Wartość max</label>
                <input
                  type="number"
                  className="form-input"
                  value={field.config.max ?? 5}
                  onChange={(e) => onUpdate({ config: { ...field.config, max: parseInt(e.target.value) } })}
                />
              </div>
            </div>
          )}

          {field.field_type === 'text' && field.config && (
            <div className="form-group">
              <label>Maks. liczba znaków</label>
              <input
                type="number"
                className="form-input"
                style={{ width: '160px' }}
                value={field.config.max_length ?? 500}
                onChange={(e) => onUpdate({ config: { ...field.config, max_length: parseInt(e.target.value) } })}
              />
            </div>
          )}

          {field.field_type === 'multiple_choice' && (
            <div className="form-group">
              <label>Opcje do wyboru <span className="required">*</span></label>
              <div className="options-editor">
                {(field.config?.options || []).map((opt, i) => (
                  <div key={i} className="option-row">
                    <input
                      type="text"
                      className="form-input option-input"
                      value={opt}
                      onChange={(e) => {
                        const opts = [...(field.config?.options || [])];
                        opts[i] = e.target.value;
                        onUpdate({ config: { ...field.config, options: opts } });
                      }}
                      placeholder={`Opcja ${i + 1}`}
                    />
                    <button
                      type="button"
                      className="option-remove-btn"
                      onClick={() => {
                        const opts = (field.config?.options || []).filter((_, idx) => idx !== i);
                        onUpdate({ config: { ...field.config, options: opts } });
                      }}
                      title="Usuń opcję"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-add-option"
                  onClick={() => {
                    const opts = [...(field.config?.options || []), ''];
                    onUpdate({ config: { ...field.config, options: opts } });
                  }}
                >
                  + Dodaj opcję
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EditSurveyForm = ({
  survey,
  formData,
  setFormData,
  departments,
  usersPerDepartment,
  onSubmit,
  onCancel,
}) => {
  const initialAssignedIdsRef = useRef(null);
  if (initialAssignedIdsRef.current === null) {
    initialAssignedIdsRef.current = new Set((survey.assigned_users || []).map(u => u.id));
  }

  return (
    <div className="create-survey-form">
      <div className="csf-topbar">
        <div className="csf-topbar-left">
          <button type="button" className="back-btn" onClick={onCancel}>← Wróć</button>
          <h3>Edytuj przypisania: {survey.name}</h3>
        </div>
      </div>

      <div className="survey-summary" style={{ margin: '0 0 16px 0', padding: '12px 16px', background: '#f8f9fa', borderRadius: '8px', fontSize: '14px' }}>
        <p style={{ margin: '2px 0' }}><strong>Okres:</strong> {new Date(survey.start_time).toLocaleDateString('pl-PL')} – {new Date(survey.end_time).toLocaleDateString('pl-PL')}</p>
        <p style={{ margin: '2px 0' }}><strong>Postęp:</strong> {survey.total_submitted}/{survey.total_assigned}</p>
      </div>

      <form onSubmit={onSubmit}>
        <RecipientSelector
          formData={formData}
          setFormData={setFormData}
          departments={departments}
          usersPerDepartment={usersPerDepartment}
          sectionNumber="1"
          initialAssignedIds={initialAssignedIdsRef.current}
        />
        <div className="form-submit-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Anuluj</button>
          <button type="submit" className="btn btn-success">Zapisz zmiany</button>
        </div>
      </form>
    </div>
  );
};

// Helper function
const getRatingColor = (val) => {
  if (val >= 4.5) return '#4caf50';
  if (val >= 3.5) return '#8bc34a';
  if (val >= 2.5) return '#ff9800';
  return '#f44336';
};

export default Surveys;
